/**
 * Formats de la liste de conversations — #82.
 *
 * Recopiés de `protoShared.tsx` plutôt qu'importés : ce fichier vit sur
 * `prototype/mobile-messaging`, une branche jetable qui ne va jamais sur `main`
 * (et il est de toute façon sous `pages/`, hors frontière).
 */

import { formatDate, langueMobile, t } from '../i18n/langue';

interface Personne {
    firstname?: string | null;
    lastname?: string | null;
    email?: string | null;
}

/** Le nom affichable, avec l'e-mail en repli — jamais une ligne vide. */
export function nomComplet(personne: Personne): string {
    const nom = `${personne.firstname ?? ''} ${personne.lastname ?? ''}`.trim();
    return nom || personne.email || t('messagerie.sansNom');
}

/** Initiales de l'avatar. Une lettre suffit quand il n'y a qu'un e-mail. */
export function initiales(personne: Personne): string {
    const prenom = (personne.firstname ?? '').trim();
    const nom = (personne.lastname ?? '').trim();
    if (prenom || nom) return `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();
    return (personne.email ?? '?').charAt(0).toUpperCase();
}

/**
 * Heure aujourd'hui, jour de la semaine dans la semaine, date au-delà.
 *
 * Même échelle que la liste web : sur une ligne où l'extrait tient en une seule
 * ligne (#16), l'horodatage doit coûter le moins de largeur possible.
 */
export function heureCourte(iso?: string | null, maintenant: Date = new Date()): string {
    if (!iso) return '';

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';

    if (date.toDateString() === maintenant.toDateString()) {
        return formatDate(date, { hour: '2-digit', minute: '2-digit' });
    }

    const hier = new Date(maintenant);
    hier.setDate(hier.getDate() - 1);
    if (date.toDateString() === hier.toDateString()) return t('messagerie.dates.hierCourt');

    const joursEcoules = Math.floor((maintenant.getTime() - date.getTime()) / 86_400_000);
    if (joursEcoules < 7) return formatDate(date, { weekday: 'short' });

    return formatDate(date, { day: '2-digit', month: '2-digit' });
}

/*
 * Formateurs construits UNE fois (audit 2026-09-23). `toLocale*String` en recrée un
 * à chaque appel, et le fil en appelle un par bulle et par séparateur, toutes les
 * 30 s. Mêmes options, donc mêmes libellés.
 *
 * Un par LANGUE : changer de langue remonte l'app, mais pas ce module — un
 * formateur figé au démarrage parlerait encore l'ancienne langue.
 */
const OPTIONS_FORMAT = {
    heure: { hour: '2-digit', minute: '2-digit' },
    jour: { weekday: 'long', day: 'numeric', month: 'long' },
    jourAnnee: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
} satisfies Record<string, Intl.DateTimeFormatOptions>;

const formateurs = new Map<string, Intl.DateTimeFormat>();

function formateur(nom: keyof typeof OPTIONS_FORMAT): Intl.DateTimeFormat {
    const langue = langueMobile();
    const cle = `${langue}:${nom}`;
    let format = formateurs.get(cle);
    if (!format) {
        format = new Intl.DateTimeFormat(langue, OPTIONS_FORMAT[nom]);
        formateurs.set(cle, format);
    }
    return format;
}

/**
 * L'heure d'une bulle — toujours l'heure (audit 2026-09-23). `heureCourte` sert la
 * LISTE, où la place manque ; dans le fil, le jour est porté par le séparateur.
 */
export function heureBulle(iso?: string | null): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return formateur('heure').format(date);
}

/** Le séparateur de jour du fil. */
export function libelleJour(iso?: string | null, maintenant: Date = new Date()): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    if (date.toDateString() === maintenant.toDateString()) return t('messagerie.dates.aujourdhui');
    const hier = new Date(maintenant);
    hier.setDate(hier.getDate() - 1);
    if (date.toDateString() === hier.toDateString()) return t('messagerie.dates.hier');
    return formateur(date.getFullYear() === maintenant.getFullYear() ? 'jour' : 'jourAnnee').format(date);
}

/**
 * HTML → texte, pour l'aperçu d'un e-mail dans le fil.
 *
 * Le texte d'un e-mail vit dans `body` (HTML), pas dans `message` : sans cette
 * conversion, la bulle n'affiche que son objet et un tiret — l'« aperçu texte
 * tronqué à 6 lignes » de #16 serait vide. Relevé en ouvrant un vrai fil.
 *
 * **`DOMParser` et non `innerHTML`.** Le second construit des nœuds dans le
 * document vivant : un `<img onerror=…>` d'un e-mail reçu s'exécuterait. Le
 * document rendu par `parseFromString` est INERTE — ni script, ni chargement de
 * ressource. Le HTML complet, lui, passe par `sanitizeEmailHtml` au moment du
 * plein écran ; ici on ne veut que du texte.
 */
export function htmlEnTexte(html?: string | null): string {
    if (!html) return '';
    try {
        // `textContent` colle les blocs sans séparateur : deux paragraphes rendent
        // « Camille,votre chambre » d'un seul tenant. On insère donc une espace aux
        // fins de bloc AVANT l'analyse. Le pire cas d'une correspondance à
        // l'intérieur d'un attribut est une espace de trop dans l'aperçu — c'est
        // bien l'analyse, et non ce remplacement, qui retire les balises.
        const espace = html.replace(/<\s*(br|\/p|\/div|\/li|\/tr|\/h[1-6])\s*\/?>/gi, ' $& ');
        const doc = new DOMParser().parseFromString(espace, 'text/html');
        return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
    } catch {
        return '';
    }
}

/** Le texte affichable d'un message, quel que soit son canal. */
export function texteMessage(message: { message?: string | null; body?: string | null }): string {
    const brut = (message.message ?? '').trim();
    return brut || htmlEnTexte(message.body);
}

/**
 * Le lien `tel:` d'un appel en un tap.
 *
 * `/customer-basics/{id}` (`CustomerMessagingResource`) n'expose que `phone` brut,
 * pas `phone_e164` — la normalisation se fait donc ici, côté client. On garde le
 * `+` international et on retire tout ce qui n'est que mise en forme visuelle :
 * espaces, points, tirets, parenthèses. `null` quand il ne reste rien à composer,
 * pour que l'appelant sache qu'il n'y a pas d'action à proposer.
 */
export function telHref(phone?: string | null): string | null {
    if (!phone) return null;
    const nettoye = phone.trim().replace(/[\s.\-()]/g, '');
    if (!nettoye) return null;
    return `tel:${nettoye}`;
}
